import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { get, isEmpty } from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Container } from '@material-ui/core'

import useTitle from 'common/use-title'
import useScid from 'common/use-scid'
import api from 'common/api'
import useCollegeFeatures from 'common/use-college-features'
import features from 'common/college-features'
import { useAppCycle, useRoundConfigurationAppCycle } from 'store/application-cycle/hooks'

import ApplicationTeaser from '../ApplicationTeaser'
import TestInitiationStep from './components/TestInitiationStep'
import TestSubmissionStep from './components/TestSubmissionStep'
import ConfirmConfigurationStep from './components/ConfirmConfigurationStep'
import ConfirmAppRoundsStep from './components/ConfirmAppRoundsStep'
import ActivateApplicationStep from './components/ActivateAppStep'
import AppManagerRoleBanner from '../Configure/components/RoleBanner'
import { isIntegrationConfigured } from '../configUtils'
import Step from './components/Step'
import Activated from './components/Activated'
import { PageTitle } from '@scoir/headers'
import { useConfigureUpcomingApplicationCycle } from 'store/feature-toggle/hooks'
import CurrentUpcomingAppCycleToggle from '../CurrentUpcomingAppCycleToggle'

const useStyles = makeStyles((theme) => ({
  componentWrapper: {
    backgroundColor: get(theme, 'palette.pageBackground.main'),
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  pageHeader: {
    [theme.breakpoints.down('xs')]: {
      wordBreak: 'break-all',
    },
  },
}))

const ApplicationIntegration = () => {
  useTitle('Scoir - Activate Application')
  const styles = useStyles()
  const scid = useScid()

  const currentAppCycle = useAppCycle()
  const upcomingCycle = useRoundConfigurationAppCycle()
  const isConfigureUpcomingEnabled = useConfigureUpcomingApplicationCycle()

  const defaultCycle = isConfigureUpcomingEnabled ? upcomingCycle : currentAppCycle
  const [focusedCycle, setApplicationCycle] = useState(defaultCycle)
  const [isCurrent, setHasCurrentFocus] = useState(isConfigureUpcomingEnabled ? false : true)

  const [currentAppConfig, setCurrentAppConfig] = useState(null)
  const [upcomingAppConfig, setUpcomingAppConfig] = useState(null)

  const focusedAppConfig = useMemo(
    () => (isConfigureUpcomingEnabled ? (isCurrent ? currentAppConfig : upcomingAppConfig) : currentAppConfig),
    [isConfigureUpcomingEnabled, currentAppConfig, isCurrent, upcomingAppConfig],
  )

  const isApplicationIntegrationEnabled = useCollegeFeatures(features.APPLICATION_INTEGRATION)
  const showTeaser = !isApplicationIntegrationEnabled || !isIntegrationConfigured(focusedAppConfig)
  const initiationVerified =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.initiationVerified
  const submissionVerified =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.submissionVerified
  const configurationConfirmed =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.configurationConfirmed
  const appRoundsConfirmed =
    focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.appRoundsConfirmed
  const activated = focusedAppConfig && focusedAppConfig.verification && focusedAppConfig.verification.activated

  const getCurrentAppConfig = useCallback(() => {
    api.get(`/api/colleges/${scid}/integrations/application/configuration/${currentAppCycle}`).then((response) => {
      setCurrentAppConfig(response)
    })
  }, [scid, currentAppCycle])

  const getUpcomingAppConfig = useCallback(() => {
    if (!isConfigureUpcomingEnabled) {
      return
    }

    api.get(`/api/colleges/${scid}/integrations/application/configuration/${upcomingCycle}`).then((response) => {
      setUpcomingAppConfig(response)
    })
  }, [isConfigureUpcomingEnabled, scid, upcomingCycle])

  const refreshFocusedAppConfig = useCallback(() => {
    if (isCurrent) {
      getCurrentAppConfig()
    } else {
      getUpcomingAppConfig()
    }
  }, [isCurrent, getCurrentAppConfig, getUpcomingAppConfig])

  useEffect(() => {
    getCurrentAppConfig()
    getUpcomingAppConfig()
  }, [getCurrentAppConfig, getUpcomingAppConfig])

  const canToggle = isConfigureUpcomingEnabled && !isEmpty(upcomingAppConfig?.id) && !isEmpty(currentAppConfig?.id)

  if (focusedAppConfig && showTeaser) {
    return <ApplicationTeaser />
  }
  return (
    <Box
      className={styles.componentWrapper}
      px={[0, 3, 5]}
      py={5}
    >
      <Container maxWidth="lg">
        <AppManagerRoleBanner />
        {!focusedAppConfig && 'Loading...'}
        {focusedAppConfig && !showTeaser && (
          <React.Fragment>
            <PageTitle
              title={`Activate ${focusedCycle - 1}-${focusedCycle} Application`}
              titleProps={{ className: styles.pageHeader }}
              actions={
                canToggle && (
                  <CurrentUpcomingAppCycleToggle
                    hasCurrentFocus={isCurrent}
                    onChangeCycleFocus={(isFocused) => {
                      setHasCurrentFocus(isFocused)
                      setApplicationCycle(isFocused ? currentAppCycle : upcomingCycle)
                    }}
                  />
                )
              }
            />
            {activated ? (
              <Activated
                applicationCycle={focusedAppConfig.applicationCycle}
                configurationId={focusedAppConfig?.id}
              />
            ) : (
              <React.Fragment>
                <Step
                  stepHeader="Step 1: Test Application Initiation"
                  cardHeader="Send and Verify Mock Application"
                  successMessage="Application Initiated"
                  isActive={!initiationVerified}
                  isComplete={initiationVerified}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }) => (
                    <TestInitiationStep
                      appConfig={focusedAppConfig}
                      refetchAppConfig={refreshFocusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 2: Test Application Submission"
                  cardHeader="Submit Supplement and Verify Status"
                  successMessage="Application Submitted"
                  isActive={initiationVerified && !submissionVerified}
                  isComplete={submissionVerified}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }) => (
                    <TestSubmissionStep
                      appConfig={focusedAppConfig}
                      refetchAppConfig={refreshFocusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 3: Confirm Application Configuration"
                  cardHeader="Configure College Application"
                  successMessage="Application Configuration Confirmed"
                  isActive={submissionVerified && !configurationConfirmed}
                  isComplete={configurationConfirmed}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }) => (
                    <ConfirmConfigurationStep
                      appConfig={focusedAppConfig}
                      refetchAppConfig={refreshFocusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 4: Confirm Application Rounds"
                  cardHeader="Update College App Rounds"
                  successMessage="Application Rounds Confirmed"
                  isActive={configurationConfirmed && !appRoundsConfirmed}
                  isComplete={appRoundsConfirmed}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep }) => (
                    <ConfirmAppRoundsStep
                      appConfig={focusedAppConfig}
                      completeStep={completeStep}
                    />
                  )}
                />
                <Step
                  stepHeader="Step 5: Activate Application"
                  cardHeader="Finalize Integration Set Up"
                  successMessage="Apply with Scoir Activated"
                  secondarySuccessMessage="Your unique access link for students will be available in August."
                  isActive={initiationVerified && submissionVerified && configurationConfirmed && appRoundsConfirmed}
                  isComplete={activated}
                  refetchAppConfig={refreshFocusedAppConfig}
                  renderStep={({ completeStep, isActive }) => (
                    <ActivateApplicationStep
                      isActive={isActive}
                      appConfigId={focusedAppConfig?.id}
                      completeStep={completeStep}
                    />
                  )}
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </Container>
    </Box>
  )
}

export default ApplicationIntegration
